import {
  QuotaGroup,
  QuotaData,
  familyColors,
  getUtilizationColor,
  getUtilizationBg,
  formatLastUpdated,
  getDataFreshness,
  getFamilyDisplayName,
} from './quotaUtils';

interface Props {
  group: QuotaGroup;
  onRequestIncrease: (quota: QuotaData, groupLabel: string, groupFamilies: string[]) => void;
}

export default function QuotaGroupCard({ group, onRequestIncrease }: Props) {
  const circumference = 2 * Math.PI * 35;
  const strokeDashoffset = circumference - (group.utilization_percent / 100) * circumference;
  const freshness = getDataFreshness(group.last_updated);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return (
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'decreasing':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
          </svg>
        );
    }
  };

  return (
    <div className={`rounded-lg border p-5 hover:shadow-lg transition-shadow md:col-span-2 col-span-full ${getUtilizationBg(group.utilization_percent)}`}>
      {/* Header row */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-white text-lg">{group.groupLabel}</h4>
          <p className="text-sm text-gray-400 mt-0.5">
            {group.families.map(f => getFamilyDisplayName(f.provider, f.instance_family)).join(' \u00b7 ')}
          </p>
        </div>

        {/* Circular progress */}
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg className="transform -rotate-90" width="80" height="80">
            <circle
              cx="40" cy="40" r="35"
              stroke="currentColor" strokeWidth="8" fill="none"
              className="text-gray-700"
            />
            <circle
              cx="40" cy="40" r="35"
              stroke="currentColor" strokeWidth="8" fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className={getUtilizationColor(group.utilization_percent)}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-lg font-bold ${getUtilizationColor(group.utilization_percent).split(' ')[0]}`}>
              {Math.round(group.utilization_percent)}%
            </span>
          </div>
        </div>
      </div>

      {/* Aggregate stats */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Total Usage</span>
          <span className="text-white font-medium">
            {group.total_usage} / {group.quota_limit}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Available</span>
          <span className="text-green-400 font-medium">{group.total_available}</span>
        </div>
        <div className="flex justify-between text-sm items-center">
          <span className="text-gray-400">Trend</span>
          <div className="flex items-center space-x-1">
            {getTrendIcon(group.usage_trend)}
            <span className="text-gray-300 capitalize">{group.usage_trend}</span>
          </div>
        </div>
      </div>

      {/* Family breakdown */}
      <div className="pt-3 border-t border-gray-700 mb-4">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Family Breakdown</p>
        <div className="space-y-2">
          {group.families.map(family => {
            const pctOfLimit = group.quota_limit > 0
              ? (family.current_usage / group.quota_limit) * 100
              : 0;
            const barColor = familyColors[family.instance_family]?.usage || 'bg-gray-600';

            return (
              <div key={family.instance_family} className="flex items-center space-x-3">
                <span className="text-xs text-gray-400 w-16 text-right font-mono">
                  {getFamilyDisplayName(family.provider, family.instance_family)}
                </span>
                <div className="flex-1 h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${barColor} rounded-full transition-all`}
                    style={{ width: `${Math.max(pctOfLimit, 1)}%` }}
                  />
                </div>
                <span className="text-xs text-white font-medium w-8 text-right">
                  {family.current_usage}
                </span>
                <span className="text-xs text-gray-500 w-10 text-right">
                  ({Math.round(pctOfLimit)}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-700">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            Updated {formatLastUpdated(group.last_updated)}
          </span>
          <span className={`text-xs ${freshness.color}`}>
            &bull; {freshness.status}
          </span>
        </div>

        {!group.quota_increase_requested && (
          <button
            onClick={() => onRequestIncrease(
              group.families[0],
              group.groupLabel,
              group.families.map(f => f.instance_family),
            )}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              group.utilization_percent >= 80
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600'
            }`}
          >
            Request Increase
          </button>
        )}

        {group.quota_increase_requested && (
          <span className="text-xs px-2 py-1 bg-yellow-900/30 text-yellow-300 border border-yellow-700 rounded flex items-center space-x-1">
            <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            <span>Increase Pending</span>
          </span>
        )}
      </div>
    </div>
  );
}
